import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import correspondenceService from '../services/correspondenceService';

export const CORRESPONDENCE_KEY = ['attorney', 'correspondence'];

/**
 * Hook to fetch all correspondence
 */
export function useCorrespondence(options = {}) {
  return useQuery({
    queryKey: [...CORRESPONDENCE_KEY, options],
    queryFn: () => correspondenceService.getCorrespondence(options),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true
  });
}

/**
 * Hook to fetch a single correspondence item
 */
export function useCorrespondenceItem(id) {
  return useQuery({
    queryKey: [...CORRESPONDENCE_KEY, id],
    queryFn: () => correspondenceService.getCorrespondenceItem(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5
  });
}

/**
 * Hook to link correspondence to an application
 */
export function useLinkCorrespondence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ correspondenceId, applicationId }) =>
      correspondenceService.linkCorrespondence(correspondenceId, applicationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CORRESPONDENCE_KEY });
    }
  });
}

/**
 * Hook to mark correspondence as processed
 */
export function useMarkProcessed() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ correspondenceId, notes }) =>
      correspondenceService.markProcessed(correspondenceId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CORRESPONDENCE_KEY });
    }
  });
}

/**
 * Hook to manual match correspondence with AI learning feedback
 */
export function useManualMatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ correspondenceId, correct, feedback }) =>
      correspondenceService.manualMatch(correspondenceId, { correct, feedback }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CORRESPONDENCE_KEY });
    }
  });
}

export default {
  useCorrespondence,
  useCorrespondenceItem,
  useLinkCorrespondence,
  useMarkProcessed,
  useManualMatch
};
